import { z } from 'zod';

const whatsappCapabilitiesSchema = z.record(z.string(), z.boolean()).default({});

export const whatsappPhoneNumberSchema = z.object({
  id: z.string().uuid(),
  whatsapp_number_id: z.string().min(1),
  phone_number_id: z.string().min(1),
  phone_number: z.string().min(1),
  /** WABA this number belongs to; null for numbers synced before multi-WABA. */
  waba_id: z.string().nullable().optional(),
  friendly_name: z.string().nullable().optional(),
  display_phone_number: z.string().nullable().optional(),
  capabilities: whatsappCapabilitiesSchema,
  is_active: z.boolean(),
  last_synced_at: z.date().or(z.string().datetime()).nullable().optional(),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
  inbound_configuration_id: z.string().uuid().nullable().optional(),
  inbound_configuration_name: z.string().nullable().optional(),
});

export type WhatsAppPhoneNumber = z.infer<typeof whatsappPhoneNumberSchema>;

export const whatsappPhoneNumberListSchema = z.array(whatsappPhoneNumberSchema);

export const whatsappNumberSyncResponseSchema = z.object({
  count: z.number().int().min(0),
  synced_at: z.date().or(z.string().datetime()),
});

export type WhatsAppNumberSyncResponse = z.infer<typeof whatsappNumberSyncResponseSchema>;

export const whatsappAssignInboundNumberSchema = z.object({
  configuration_id: z.string().uuid().nullable(),
});

export type WhatsAppAssignInboundNumberDto = z.infer<typeof whatsappAssignInboundNumberSchema>;

// ── WhatsApp Business Accounts (WABAs) ────────────────────────────
// Lets one platform serve numbers from several business managers. Each WABA may
// carry its own system-user token; without one we fall back to the global
// WHATSAPP_ACCESS_TOKEN. Every WABA must be on the SAME Meta app — the webhook
// is verified with the global app secret / verify token.

export const whatsappBusinessAccountSchema = z.object({
  id: z.string().uuid(),
  waba_id: z.string().min(1),
  name: z.string().nullable().optional(),
  /** The token itself is never returned — only whether one is set. */
  has_token: z.boolean(),
  is_active: z.boolean(),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
});

export type WhatsAppBusinessAccount = z.infer<typeof whatsappBusinessAccountSchema>;

export const whatsappBusinessAccountListSchema = z.array(whatsappBusinessAccountSchema);

export const createWhatsappBusinessAccountSchema = z.object({
  waba_id: z.string().min(1, 'WABA ID is required'),
  name: z.string().max(255).optional().nullable(),
  /** Leave empty to use the global WHATSAPP_ACCESS_TOKEN for this WABA. */
  access_token: z.string().optional().nullable(),
  // No zod .default() here on purpose: it would make `is_active` required in the
  // inferred type. The service defaults it (`payload.is_active ?? true`).
  is_active: z.boolean().optional(),
});

export type CreateWhatsAppBusinessAccountDto = z.infer<typeof createWhatsappBusinessAccountSchema>;

export const updateWhatsappBusinessAccountSchema = z.object({
  waba_id: z.string().min(1).optional(),
  name: z.string().max(255).optional().nullable(),
  /** Omit to leave unchanged; empty string clears it (falls back to global). */
  access_token: z.string().optional().nullable(),
  is_active: z.boolean().optional(),
});

export type UpdateWhatsAppBusinessAccountDto = z.infer<typeof updateWhatsappBusinessAccountSchema>;

/** Result of subscribing our Meta app to a WABA's webhooks. */
export const whatsappSubscribeWebhooksResponseSchema = z.object({
  success: z.boolean(),
  /** Names of the apps now receiving this WABA's webhooks. */
  apps: z.array(z.string()),
});

export type WhatsAppSubscribeWebhooksResponse = z.infer<
  typeof whatsappSubscribeWebhooksResponseSchema
>;
